import { useMemo, useState } from 'react'
import { ChevronRight, Plus, X } from 'lucide-react'
import type {
  BasketItem,
  CalculatorDictionary,
  CalculatorMetadata,
} from '@entities/calculator'
import { useToggleSavedRecipeMutation } from '@entities/calculator'
import { CALC_EVENTS, useTrackAction } from './analytics'
import RecipeCostSheet from './RecipeCostSheet'
import RecipeLibrarySheet from './RecipeLibrarySheet'

export interface RecipeCardOption {
  cardId: number
  title: string
  icon: string
}

interface RecipesTabProps {
  /** Все рецепты с калькулятором — общий список, из него набирают себе. */
  cards: readonly RecipeCardOption[]
  /** Что человек добавил себе, в порядке бэка: новые сверху. */
  savedIds: readonly number[]
  dictionary: CalculatorDictionary | undefined
  metadata: CalculatorMetadata | undefined
  basketItems: readonly BasketItem[]
  isLoading: boolean
  onGoToRates: () => void
}

/**
 * «Мои рецепты» — те, по которым человек считает себестоимость. Список личный,
 * его же отдаёт `/calculator/saved`; общий каталог живёт в отдельной шторке.
 *
 * Состав грузится при открытии карточки, а не списком: батч ограничен 20
 * рецептами, а сохранить можно сколько угодно.
 */
const RecipesTab = ({
  cards,
  savedIds,
  dictionary,
  metadata,
  basketItems,
  isLoading,
  onGoToRates,
}: RecipesTabProps) => {
  const [openCardId, setOpenCardId] = useState<number | null>(null)
  const [isLibraryOpen, setLibraryOpen] = useState(false)
  // Удаление подтверждается вторым нажатием: вместе с рецептом бэк удаляет и
  // часы работы, а вернуть рецепт мало — часы придётся вводить заново.
  const [confirmingId, setConfirmingId] = useState<number | null>(null)
  const toggleSaved = useToggleSavedRecipeMutation()
  const track = useTrackAction()

  const toggleRecipe = (cardId: number, next: boolean) => {
    toggleSaved.mutate({ cardId, next })
    // Отправляем по факту нажатия, а не по ответу: ручки отвечают без тела,
    // а отказ и так виден — список вернётся прежним.
    if (next) track(CALC_EVENTS.saveRecipe, String(cardId))
  }

  const cardById = useMemo(
    () => new Map(cards.map(card => [card.cardId, card])),
    [cards]
  )

  // Порядок сохранённых задаёт бэк — новые первыми, пересортировывать не надо.
  // Карточка со снятым калькулятором из ответа выпадает, так что подписать её
  // нечем — такие пропускаем.
  const saved = useMemo(
    () =>
      savedIds
        .map(cardId => cardById.get(cardId))
        .filter((card): card is RecipeCardOption => Boolean(card)),
    [savedIds, cardById]
  )

  const savedIdSet = useMemo(() => new Set(savedIds), [savedIds])
  const openCard =
    openCardId === null ? null : (cardById.get(openCardId) ?? null)

  return (
    <div className="cost-tab">
      <p className="cost-tab__lead">
        Рецепты, по которым вы считаете. Откройте любой — посчитаем
        себестоимость по вашим ценам из «Моих продуктов».
      </p>

      {isLoading && <p className="cost-note">Загружаем рецепты…</p>}

      {!isLoading && saved.length === 0 && (
        <p className="cost-note">
          Пока пусто. Добавьте рецепты, по которым считаете себестоимость, — они
          будут ждать вас здесь.
        </p>
      )}

      <div className="cost-recipes">
        {saved.map(card => (
          <div key={card.cardId} className="cost-recipe-row">
            <button
              type="button"
              className="cost-recipe"
              onClick={() => {
                track(CALC_EVENTS.openRecipeCost, String(card.cardId))
                setOpenCardId(card.cardId)
              }}
            >
              {card.icon ? (
                <img
                  src={card.icon}
                  alt=""
                  loading="lazy"
                  className="cost-recipe__cover"
                />
              ) : (
                <span className="cost-recipe__cover" />
              )}
              <span className="cost-recipe__title">{card.title}</span>
              <ChevronRight size={18} aria-hidden />
            </button>

            {confirmingId === card.cardId ? (
              <button
                type="button"
                className="cost-recipe__confirm"
                onClick={() => {
                  setConfirmingId(null)
                  toggleRecipe(card.cardId, false)
                }}
              >
                Убрать вместе с часами
              </button>
            ) : (
              <button
                type="button"
                className="cost-recipe__remove"
                onClick={() => setConfirmingId(card.cardId)}
                aria-label={`Убрать ${card.title} из моих рецептов`}
              >
                <X size={16} aria-hidden />
              </button>
            )}
          </div>
        ))}
      </div>

      {toggleSaved.isError && (
        <p className="cost-note warning">
          Не удалось обновить список рецептов. Попробуйте ещё раз.
        </p>
      )}

      <button
        type="button"
        className="cost-button wide"
        onClick={() => setLibraryOpen(true)}
        disabled={cards.length === 0}
      >
        <Plus size={18} aria-hidden />
        Добавить рецепт
      </button>

      {isLibraryOpen && (
        <RecipeLibrarySheet
          cards={cards}
          savedIds={savedIdSet}
          onToggle={toggleRecipe}
          onClose={() => setLibraryOpen(false)}
        />
      )}

      {openCard && (
        <RecipeCostSheet
          cardId={openCard.cardId}
          title={openCard.title}
          dictionary={dictionary}
          metadata={metadata}
          basketItems={basketItems}
          onClose={() => setOpenCardId(null)}
          onGoToRates={onGoToRates}
        />
      )}
    </div>
  )
}

export default RecipesTab
